import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../database/firebase';

// Async thunk to generate next bill number
export const generateBillNumber = createAsyncThunk(
  'recharge/generateBillNumber',
  async (_, { rejectWithValue }) => {
    try {
      const rechargesRef = collection(db, 'recharges');
      const q = query(rechargesRef, orderBy('billNo', 'desc'));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return 'BILL-0001';
      }

      const lastBill = querySnapshot.docs[0].data();
      const lastBillNo = lastBill.billNo;
      const billNumber = parseInt(lastBillNo.split('-')[1]);
      const newBillNo = `BILL-${String(billNumber + 1).padStart(4, '0')}`;

      return newBillNo;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk to save recharge
export const saveRecharge = createAsyncThunk(
  'recharge/saveRecharge',
  async (rechargeData, { rejectWithValue }) => {
    try {
      // Save recharge document
      const rechargesRef = collection(db, 'recharges');
      const docRef = await addDoc(rechargesRef, {
        ...rechargeData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Update user's shaketopup array and body metrics
      if (rechargeData.userId) {
        const userRef = doc(db, 'users', rechargeData.userId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          const updateData = {};

          // Update shake topup if quantity > 0
          if (rechargeData.shakeQuantity > 0) {
            const currentShakeTopup = userData.shaketopup || { totalshakequantity: 0, orders: [] };
            const currentTotal = parseInt(currentShakeTopup.totalshakequantity || 0);

            // Create new order entry
            const newOrder = {
              date: rechargeData.billDate || new Date().toISOString().split('T')[0],
              billNo: rechargeData.billNo,
              products: rechargeData.productItems.map((item) => ({
                productid: item.productId || item.productCode,
                productName: item.productName,
                quantity: item.quantity,
                productprice: item.mrp,
                totalprice: item.total,
              })),
              quantity: rechargeData.shakeQuantity,
              totalPrice: rechargeData.total,
              salesType: rechargeData.salesType,
            };

            // Update shaketopup structure
            updateData.shaketopup = {
              totalshakequantity: currentTotal + rechargeData.shakeQuantity,
              orders: [...(currentShakeTopup.orders || []), newOrder],
            };
          }

          // Update body metrics (dailyrecord) if any metrics are provided
          const metrics = rechargeData.bodyMetrics;
          const hasMetrics =
            metrics &&
            (metrics.weight ||
              metrics.fat ||
              metrics.visceralFat ||
              metrics.muscles ||
              metrics.bmr ||
              metrics.bmi ||
              metrics.bodyAge);

          if (hasMetrics) {
            const currentDailyRecord = userData.dailyrecord || { average: '', records: [] };

            // Create new body metrics record
            const newMetricsRecord = {
              date: metrics.date || new Date().toISOString().split('T')[0],
              weight: parseFloat(metrics.weight) || '',
              fat: parseFloat(metrics.fat) || '',
              visceralFat: parseFloat(metrics.visceralFat) || '',
              muscles: parseFloat(metrics.muscles) || '',
              bmr: parseFloat(metrics.bmr) || '',
              bmi: parseFloat(metrics.bmi) || '',
              bodyAge: parseFloat(metrics.bodyAge) || '',
            };

            // Calculate average weight if there are records
            const allRecords = [...(currentDailyRecord.records || []), newMetricsRecord];
            const validWeights = allRecords
              .filter((r) => r.weight)
              .map((r) => parseFloat(r.weight));
            const averageWeight =
              validWeights.length > 0
                ? (validWeights.reduce((sum, w) => sum + w, 0) / validWeights.length).toFixed(2)
                : '';

            updateData.dailyrecord = {
              average: averageWeight,
              records: allRecords,
            };
          }

          // Apply updates if there's anything to update
          if (Object.keys(updateData).length > 0) {
            updateData.updatedAt = new Date().toISOString();
            await updateDoc(userRef, updateData);
          }
        }
      }

      return { id: docRef.id, ...rechargeData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk to fetch user by ID
export const fetchUserById = createAsyncThunk(
  'recharge/fetchUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      } else {
        return rejectWithValue('User not found');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  // Bill Information
  billNo: '',
  billDate: new Date().toISOString().split('T')[0],

  // User Selection
  selectedUser: null,
  selectedSalesType: '',

  // Body Metrics
  bodyMetrics: {
    weight: '',
    fat: '',
    visceralFat: '',
    muscles: '',
    bmr: '',
    bmi: '',
    bodyAge: '',
    date: new Date().toISOString().split('T')[0],
  },

  // Product Items
  productItems: [],

  // Payment
  paymentMethod: 'cash',

  // Calculations
  subtotal: 0,
  total: 0,

  // Loading & Error States
  isLoading: false,
  isSaving: false,
  error: null,
  success: false,
};

const rechargeSlice = createSlice({
  name: 'recharge',
  initialState,
  reducers: {
    // Set selected user
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },

    // Set sales type
    setSalesType: (state, action) => {
      state.selectedSalesType = action.payload;

      // If donation type selected, set total to 0 and payment to cash
      if (action.payload === 'donation') {
        state.total = 0;
        state.paymentMethod = 'cash';
      } else {
        // Recalculate normal total
        state.total = state.subtotal;
      }
    },

    // Set body metrics
    setBodyMetrics: (state, action) => {
      state.bodyMetrics = { ...state.bodyMetrics, ...action.payload };
    },

    // Add product item
    addProductItem: (state, action) => {
      state.productItems.push({
        id: Date.now(),
        productCode: '',
        productName: '',
        quantity: 1,
        mrp: 0,
        total: 0,
        ...action.payload,
      });
    },

    // Update product item
    updateProductItem: (state, action) => {
      const { index, field, value } = action.payload;
      if (state.productItems[index]) {
        state.productItems[index][field] = value;

        // Calculate item total
        if (field === 'quantity' || field === 'mrp') {
          const item = state.productItems[index];
          item.total = item.quantity * item.mrp;
        }

        // Recalculate totals and shake quantity
        state.subtotal = state.productItems.reduce((sum, item) => sum + (item.total || 0), 0);
        state.shakeQuantity = state.productItems.reduce(
          (sum, item) => sum + (parseInt(item.quantity) || 0),
          0,
        );

        // Set total based on sales type
        if (state.selectedSalesType === 'donation') {
          state.total = 0;
        } else {
          state.total = state.subtotal;
        }
      }
    },

    // Auto-fill product details
    autoFillProductItem: (state, action) => {
      const { index, product } = action.payload;
      if (state.productItems[index] && product) {
        state.productItems[index] = {
          ...state.productItems[index],
          productCode: product.productCode || product.id,
          productName: product.productName || product.name,
          mrp: product.mrp || product.price || 0,
          total: state.productItems[index].quantity * (product.mrp || product.price || 0),
          productId: product.id || product.productCode,
        };

        // Recalculate totals and shake quantity
        state.subtotal = state.productItems.reduce((sum, item) => sum + (item.total || 0), 0);
        state.shakeQuantity = state.productItems.reduce(
          (sum, item) => sum + (parseInt(item.quantity) || 0),
          0,
        );

        // Set total based on sales type
        if (state.selectedSalesType === 'donation') {
          state.total = 0;
        } else {
          state.total = state.subtotal;
        }
      }
    },

    // Remove product item
    removeProductItem: (state, action) => {
      state.productItems.splice(action.payload, 1);

      // Recalculate totals
      state.subtotal = state.productItems.reduce((sum, item) => sum + (item.total || 0), 0);

      // Set total based on sales type
      if (state.selectedSalesType === 'donation') {
        state.total = 0;
      } else {
        state.total = state.subtotal;
      }
    },

    // Set payment method
    setPaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
    },

    // Set bill number
    setBillNo: (state, action) => {
      state.billNo = action.payload;
    },

    // Set bill date
    setBillDate: (state, action) => {
      state.billDate = action.payload;
    },

    // Calculate totals
    calculateTotals: (state) => {
      state.subtotal = state.productItems.reduce((sum, item) => sum + (item.total || 0), 0);

      // Set total based on sales type
      if (state.selectedSalesType === 'donation') {
        state.total = 0;
      } else {
        state.total = state.subtotal;
      }
    },

    // Reset form
    resetRechargeForm: (state) => {
      return {
        ...initialState,
        billNo: state.billNo, // Keep bill number
      };
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // Generate Bill Number
    builder
      .addCase(generateBillNumber.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(generateBillNumber.fulfilled, (state, action) => {
        state.isLoading = false;
        state.billNo = action.payload;
        state.error = null;
      })
      .addCase(generateBillNumber.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Save Recharge
    builder
      .addCase(saveRecharge.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(saveRecharge.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = true;
        state.error = null;
      })
      .addCase(saveRecharge.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
        state.success = false;
      });

    // Fetch User By ID
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload;
        state.previousShakeQuantity = action.payload?.previousShakeQuantity || 0;
        state.error = null;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedUser,
  setSalesType,
  setBodyMetrics,
  addProductItem,
  updateProductItem,
  autoFillProductItem,
  removeProductItem,
  setPaymentMethod,
  setBillNo,
  setBillDate,
  calculateTotals,
  resetRechargeForm,
  clearError,
} = rechargeSlice.actions;

export default rechargeSlice.reducer;
